import api from "../services/authService";

/**
 * Cost-analysis API client.
 *
 * Wraps the three super-admin-only endpoints exposed by the `cost_tracking`
 * Django app (`/api/cost-analysis/*`). Backend reference: `dms_backend`
 * PR #358 (Phase 1 — LLM call recording + per-matter aggregation) + PR #396
 * (Phase 2 — external cloud-billing ingestion into `InfrastructureCostRecord`).
 *
 * Conventions:
 *   - Functions return the raw axios response so callers can inspect
 *     `response.status` / `response.data` directly.
 *   - All endpoints support either `?days=N` (default 30, valid 1-365)
 *     OR `?start=YYYY-MM-DD&end=YYYY-MM-DD` (inclusive start, exclusive end).
 *     If both supplied, start/end win on the backend.
 *   - Money values come back as strings ("412.385000") — backend uses
 *     `decimal.Decimal` for financial accuracy. Parse with `parseFloat`
 *     ONLY for display formatting.
 *   - Auth: `IsSuperAdmin` (currently `user.role === 'admin'`). Anonymous
 *     or non-admin users get a 403 — caller should render the friendly
 *     empty state, not a hard error.
 *   - All three endpoints are read-only and idempotent. Safe to retry on
 *     5xx, but DON'T auto-poll: data updates either at LLM-call recording
 *     time (continuous, sub-cent) or on the daily 12:00 UTC cloud-billing
 *     ingestion job. A manual refresh button is sufficient.
 */

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAYS_MIN = 1;
const DAYS_MAX = 365;

class CostAnalysisQueryError extends Error {
  constructor(message) {
    super(message);
    this.name = "CostAnalysisQueryError";
  }
}

const isValidIsoDate = (value) => {
  if (typeof value !== "string" || !ISO_DATE_RE.test(value)) return false;
  // Reject impossible calendar values (e.g. 2026-13-40) by round-tripping
  // through Date and confirming the parts match what we put in. Date.UTC
  // accepts overflow silently, so the canonical-string compare is the
  // load-bearing check.
  const [y, m, d] = value.split("-").map((n) => Number(n));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
};

/**
 * Build the query string the three endpoints share.
 *
 * Validates client-side so a typo in the date picker doesn't burn a
 * round trip and surface as an opaque 400 from the server. Throws a
 * `CostAnalysisQueryError` on bad input — callers can match on
 * `error.name === "CostAnalysisQueryError"` to distinguish from network
 * failures.
 *
 *   buildCostAnalysisParams()                 → "?days=30"
 *   buildCostAnalysisParams({ days: 7 })      → "?days=7"
 *   buildCostAnalysisParams({ start, end })   → "?start=YYYY-MM-DD&end=YYYY-MM-DD"
 */
export const buildCostAnalysisParams = ({ days, start, end } = {}) => {
  // start/end take precedence over days (matches backend behavior).
  if (start || end) {
    if (!start || !end) {
      throw new CostAnalysisQueryError(
        "start and end must both be provided (YYYY-MM-DD)"
      );
    }
    if (!isValidIsoDate(start) || !isValidIsoDate(end)) {
      throw new CostAnalysisQueryError(
        "start and end must be valid YYYY-MM-DD dates"
      );
    }
    if (start >= end) {
      throw new CostAnalysisQueryError("start must be strictly before end");
    }
    return `?start=${start}&end=${end}`;
  }

  const n = days == null ? 30 : Number(days);
  if (!Number.isInteger(n) || n < DAYS_MIN || n > DAYS_MAX) {
    throw new CostAnalysisQueryError(
      `days must be an integer between ${DAYS_MIN} and ${DAYS_MAX}`
    );
  }
  return `?days=${n}`;
};

/**
 * GET the global cost-analysis rollup across all matters and infrastructure.
 *
 * Returns the full payload: summary, by_matter, by_model, by_operation,
 * by_day, infra_by_provider, infra_by_service.
 *
 * Use this for the Cost Analysis dashboard's global view.
 */
export const getGlobalCostAnalysis = (params = {}) => {
  // Convert any synchronous validation throw into a rejected promise so
  // callers can rely on a uniform `.catch` / `await` surface.
  let qs;
  try {
    qs = buildCostAnalysisParams(params);
  } catch (err) {
    return Promise.reject(err);
  }
  return api.get(`/cost-analysis/${qs}`);
};

/**
 * GET the per-matter cost detail for a single matter.
 *
 * Same shape as global minus `by_matter`, plus an `allocation` object
 * explaining how this matter's slice of shared overhead was computed.
 * On this endpoint, `infra_by_provider` and `infra_by_service` rows
 * include BOTH `direct_cost_usd` AND `allocated_cost_usd` (where
 * `cost_usd = direct + allocated`); on the global endpoint those keys
 * are absent because every dollar in the global infra view is "direct."
 */
export const getMatterCostAnalysis = (matterId, params = {}) => {
  if (matterId == null || matterId === "") {
    return Promise.reject(
      new CostAnalysisQueryError("matterId is required")
    );
  }
  let qs;
  try {
    qs = buildCostAnalysisParams(params);
  } catch (err) {
    return Promise.reject(err);
  }
  return api.get(`/cost-analysis/matters/${matterId}/${qs}`);
};

export const getIntakeChannelCost = (channel, params = {}) => {
  if (!["mail", "fax"].includes(channel)) {
    return Promise.reject(
      new CostAnalysisQueryError("channel must be mail or fax")
    );
  }
  let qs;
  try {
    qs = buildCostAnalysisParams(params);
  } catch (err) {
    return Promise.reject(err);
  }
  const separator = qs.startsWith("?") ? "&" : "?";
  return api.get(
    `/cost-analysis/intake-channel/${qs}${separator}channel=${channel}`
  );
};

/**
 * GET the lightweight per-model cost rollup. Optional secondary view —
 * not used by the v1 dashboard but exposed here so future surfaces
 * (per-model cost dashboard, model-cost regression alerts) don't have
 * to re-derive the URL shape.
 */
export const getModelCostAnalysis = (params = {}) => {
  let qs;
  try {
    qs = buildCostAnalysisParams(params);
  } catch (err) {
    return Promise.reject(err);
  }
  return api.get(`/cost-analysis/models/${qs}`);
};

export { CostAnalysisQueryError };
